import * as WWW from "../utils/WWWFunctions";
import * as THREE from "three";

import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";
import { FXAAShader } from "three/addons/shaders/FXAAShader.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { GammaCorrectionShader } from "three/examples/jsm/shaders/GammaCorrectionShader.js";

import fragmentShader from "../../shaders/fragment.glsl";
import vertexShader from "../../shaders/vertex.glsl";
import GUI from "lil-gui";

export default class ThreeCanvas {
  constructor(wrapperDOM) {
    this.parentDOM = wrapperDOM;
    this.sizes = {
      width: this.parentDOM.clientWidth,
      height: this.parentDOM.clientHeight,
    };
    this.canvasImage = this.parentDOM.dataset.canvasImage || null;
    this.canvas = document.createElement("canvas");
    this.parentDOM.appendChild(this.canvas);
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xffffff);
    this.clock = new THREE.Clock();

    this.fpsInterval = 1000 / 60;
    this.then = 0;
    this.now = 0;
    this.elapsed = 0;

    this.initControls = false;
    this.panoMesh = null;
    window.addEventListener("resize", () => this.resize());
  }

  async init() {
    await this.createElements();
    this.setupCamera();
    this.setupRenderer();
    this.setupEffectComposer();
    this.setupLights();
    // this.setupOrbitControls();
    // this.setupGUI();
    this.startRendering();
  }

  setupCamera() {
    this.camera = createCamera.bind(this)(
      "perspective",
      this.sizes.width / this.sizes.height,
    );
    // place the camera near the origin so it's inside the panorama sphere
    this.camera.position.set(0, 0, 0.1);
    this.scene.add(this.camera);
  }

  setupGUI() {
    if (this.gui) {
      try {
        this.gui.destroy();
      } catch (e) {
        /* ignore */
      }
    }
    this.gui = new GUI();

    const panoFolder = this.gui.addFolder("Panorama");
    panoFolder.open();

    // expose only rotation X (pitch) and rotation Y (yaw) for the panorama mesh
    const initial = {
      rotationX: this.panoMesh ? this.panoMesh.rotation.x : 0,
      rotationY: this.panoMesh ? this.panoMesh.rotation.y : 0,
    };

    panoFolder
      .add(initial, "rotationX", -Math.PI / 2, Math.PI / 2)
      .name("Rotation X")
      .step(0.001)
      .onChange((v) => {
        if (this.panoMesh) this.panoMesh.rotation.x = v;
      });

    panoFolder
      .add(initial, "rotationY", -Math.PI, Math.PI)
      .name("Rotation Y")
      .step(0.001)
      .onChange((v) => {
        if (this.panoMesh) this.panoMesh.rotation.y = v;
      });

    // small helper to sync GUI with mesh if mesh is created later
    this._syncPanoGUI = () => {
      if (!this.panoMesh) return;
      initial.rotationX = this.panoMesh.rotation.x;
      initial.rotationY = this.panoMesh.rotation.y;
    };
  }

  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });

    this.renderer.autoClearColor = true;
    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    this.renderer.setClearColor(0x000000, 0);
  }

  setupEffectComposer() {
    this.effectComposer = new EffectComposer(this.renderer);

    const renderPass = new RenderPass(this.scene, this.camera);
    this.effectComposer.addPass(renderPass);

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(this.sizes.width, this.sizes.height),
      1.5,
      0.4,
      0.85,
    );
    bloomPass.threshold = 0;
    bloomPass.strength = 0.75;
    bloomPass.radius = 1.5;
    // this.effectComposer.addPass(bloomPass);

    const gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
    this.effectComposer.addPass(gammaCorrectionPass);

    const effectFXAA = new ShaderPass(FXAAShader);
    effectFXAA.uniforms["resolution"].value.set(
      1 / this.sizes.width,
      1 / this.sizes.height,
    );
    this.effectComposer.addPass(effectFXAA);
  }

  setupLights() {
    const hemiLight = new THREE.HemisphereLight();
    hemiLight.name = "hemi_light";
    this.scene.add(hemiLight);

    const light1 = new THREE.AmbientLight(0xffffff, 0.3);
    light1.name = "ambient_light";
    this.camera.add(light1);

    const light2 = new THREE.DirectionalLight(0xffffff, 2.5);
    light2.position.set(0.5, 0, 0.866); // ~60º
    light2.name = "main_light";
    this.camera.add(light2);

    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    pmremGenerator.compileEquirectangularShader();
    const neutralEnvironment = pmremGenerator.fromScene(
      new RoomEnvironment(),
    ).texture;
    this.scene.environment = neutralEnvironment;

    this.renderer.toneMapping = Number(THREE.LinearToneMapping);
    this.renderer.toneMappingExposure = Math.pow(2, 0.0);
  }

  setupOrbitControls() {
    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enabled = true;
    this.controls.enableDamping = true;
    // for a 360 panorama viewer, disable panning and lock the distance
    this.controls.enablePan = false;
    this.controls.enableZoom = false;
    this.controls.autoRotate = false;
    this.controls.autoRotateSpeed = 2;
    // keep the camera at a fixed (small) distance from the center so rotation
    // feels like looking around from a single point inside the sphere
    const fixedDistance = this.camera.position.length() || 0.1;
    this.controls.minDistance = fixedDistance;
    this.controls.maxDistance = fixedDistance;
  }

  async createElements() {
    this.loader = new THREE.TextureLoader();
    // load texture and wait for it to be ready
    this.texture = await new Promise((resolve, reject) => {
      this.loader.load(
        this.canvasImage || `${root}/assets/images/location-pano.jpg`,
        (tex) => resolve(tex),
        undefined,
        (err) => reject(err),
      );
    });

    // correct color encoding
    this.texture.colorSpace = THREE.SRGBColorSpace;

    // create an inward-facing sphere and apply the equirectangular texture
    const radius = 1;
    const widthSegments = 32;
    const heightSegments = 32;
    const sphereGeo = new THREE.SphereGeometry(
      radius,
      widthSegments,
      heightSegments,
    );
    sphereGeo.scale(-1, 1, 1);

    const sphereMat = new THREE.MeshBasicMaterial({ map: this.texture });
    this.panoMesh = new THREE.Mesh(sphereGeo, sphereMat);
    this.panoMesh.name = "pano_sphere";
    this.scene.add(this.panoMesh);
    console.log("Panorama mesh created and added to scene", this.panoMesh);
  }

  startRendering() {
    this.then = window.performance.now();
    this.render();
  }

  render() {
    window.requestAnimationFrame(() => {
      this.render();
    });

    this.now = window.performance.now();
    this.elapsed = this.now - this.then;

    if (this.elapsed > this.fpsInterval) {
      this.then = this.now - (this.elapsed % this.fpsInterval);

      if (this.controls && this.controls.enabled) {
        this.controls.update();
      }
      if (this.effectComposer != undefined) {
        this.effectComposer.render();
      } else {
        this.renderer.render(this.scene, this.camera);
      }

      const deltaTime = this.clock.getElapsedTime();

      // this.responsiveCamera();
    }
  }

  responsiveCamera() {
    const MIN_WIDTH = 375;
    const MIN_HEIGHT = 667;
    const MAX_WIDTH = 1920;
    const MAX_HEIGHT = 1080;

    const MIN_FOV = 25;
    const MAX_FOV = 20;
    let newFOV = Math.floor(
      WWW.valueMap(
        this.sizes.width / this.sizes.height,
        MIN_WIDTH / MIN_HEIGHT,
        MAX_WIDTH / MAX_HEIGHT,
        MIN_FOV,
        MAX_FOV,
      ),
    );
    this.camera.setFocalLength(newFOV);
  }

  resize() {
    this.sizes.width = this.parentDOM.clientWidth;
    this.sizes.height = this.parentDOM.clientHeight;
    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));

    if (this.effectComposer) {
      this.effectComposer.setSize(this.sizes.width, this.sizes.height);
      this.effectComposer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    }

    resizeCamera.bind(this)(
      this.camera,
      this.sizes.width / this.sizes.height,
      this.scene,
    );
  }
}

function createCamera(type, aspectRatio) {
  let camera = undefined;
  switch (type) {
    case "perspective":
      camera = new THREE.PerspectiveCamera(65, aspectRatio, 0.1, 100);
      break;
    case "orthographic":
      camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, 1000);
      break;
    default:
      camera = new THREE.PerspectiveCamera(75, aspectRatio, 0.1, 100);
  }
  return camera;
}

function resizeCamera(camera, aspectRatio, object3D = null) {
  if (camera.type == "OrthographicCamera") {
    let boundingBox = new THREE.Box3().setFromObject(object3D);
    let size = new THREE.Vector3();
    boundingBox.getSize(size);
    camera.left = size.x / -2;
    camera.right = size.x / 2;
    camera.top = size.y / 2;
    camera.bottom = size.y / -2;
    camera.updateProjectionMatrix();
  } else {
    camera.aspect = aspectRatio;
    camera.updateProjectionMatrix();
  }
}
